import classNames from "classnames";
import { Entry } from "contentful";
import dynamic from "next/dynamic";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import { useBreakpoint } from "~/contexts/breakpoint";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import useScrollDirection from "~/utils/use-scroll-direction";

import styles from "./editorial-cards.module.scss";
const HighlightedText = dynamic(() => import("~/components/common/highlighted-text"));
const RichText = dynamic(() => import("~/components/common/rich-text"));
const EditorialCard = dynamic(() => import("./editorial-card"));
const FilterCardsDesktop = dynamic(() => import("./filter-cards-desktop"));
const FilterCardsMobile = dynamic(() => import("./filter-cards-mobile"));

type Props = {
  entry: Entry<unknown>;
};

export default function EditorialCards({ entry }: Props) {
  const filterAllTag = Constants.FILTER_CARDS_STANDARD.all;
  const filterEditorialTag = Constants.FILTER_CARDS_STANDARD.editorial;
  const filterVideoTag = Constants.FILTER_CARDS_STANDARD.video;
  const filterTutorialTypeTag = Constants.FILTER_CARDS_STANDARD.tutorialType;

  assertEditorialType<Editorials.EditorialCards>(entry, "editorialCards");
  const { title, paragraph, disableFirstBoxOfCardFilter, cardFilter, cards, firstCardIsHighlighted } = entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(entry);

  const activeCards = useMemo(() => cards?.filter((c) => isActiveEntry(c)), [cards]);

  const breakpoint = useBreakpoint();

  const { dir } = useScrollDirection({
    initialDirection: "up",
    thresholdPixels: breakpoint === "desktop" ? Constants.HEADER_HEIGHT_DESKTOP : Constants.HEADER_HEIGHT_MOBILE,
  });

  const { control, setValue, reset } = useForm();
  const watchedFields = useWatch({ control });

  const activeFilters = useMemo(() => {
    return Object.entries(watchedFields)
      .filter(([_, value]) => value)
      .map(([name, value]) => {
        if (name === filterTutorialTypeTag && value === "all") {
          return filterAllTag;
        }
        if (name === filterTutorialTypeTag && value === "video") {
          return filterVideoTag;
        }
        return name;
      });
  }, [watchedFields, filterAllTag, filterVideoTag, filterTutorialTypeTag]);

  const quantityFilters = useMemo(() => {
    const result: Record<string, number> = {};

    activeCards?.forEach((card) => {
      const contentfulTags = card.metadata.tags;
      let tags: string[] = [];
      let hideCard: boolean = false;

      if (contentfulTags.find((tag) => tag.sys.id === filterVideoTag)) {
        tags = contentfulTags.map((tag) => tag.sys.id).concat(filterAllTag);
      } else {
        tags = contentfulTags.map((tag) => tag.sys.id).concat([filterAllTag, filterEditorialTag]);
      }

      hideCard = activeFilters.length > 0 && !activeFilters.every((filterId) => tags.includes(filterId));

      if (!hideCard) {
        tags.forEach((tag) => {
          if (result[tag]) {
            result[tag]++;
          } else {
            result[tag] = 1;
          }
        });
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCards, activeFilters]);

  useEffect(() => {
    if (breakpoint === "mobile") {
      reset();
      setValue("filterTutorialType", "all");
    } else if (breakpoint === "desktop") {
      reset();
    }
  }, [breakpoint, reset, setValue]);

  const handleReset = () => {
    if (breakpoint === "mobile") {
      reset();
      setValue("filterTutorialType", "all");
    } else if (breakpoint === "desktop") {
      reset();
    }
  };

  const handlePartialReset = (fields: string[]) => {
    fields.forEach((field) => setValue(field, undefined));
  };

  return (
    <Container className={styles.container}>
      {title ? (
        <h2
          {...inspectorMode?.getProps("title")}
          className={classNames(
            styles.titleEditorialCards,
            cardFilter ? styles.titleEditorialCardsWithFilters : undefined
          )}
        >
          <HighlightedText text={title} />
        </h2>
      ) : null}
      {paragraph ? (
        <div {...inspectorMode?.getProps("paragraph")} className={styles.paragraph}>
          <RichText text={paragraph} />
        </div>
      ) : null}
      <Grid className={classNames(styles.gridCards, cardFilter ? styles.gridCardsWithFilters : undefined)}>
        <>
          {cardFilter && activeCards && (
            <>
              <FilterCardsDesktop
                activeCards={activeCards}
                cardFilter={cardFilter}
                quantityFilters={quantityFilters}
                activeFilters={activeFilters}
                scrollUp={dir === "up"}
                control={control}
                handleReset={handleReset}
                handlePartialReset={handlePartialReset}
                disableFirstBoxOfCardFilter={disableFirstBoxOfCardFilter}
              />

              <FilterCardsMobile
                activeCards={activeCards}
                cardFilter={cardFilter}
                quantityFilters={quantityFilters}
                activeFilters={activeFilters}
                scrollUp={dir === "up"}
                control={control}
                handleReset={handleReset}
                handlePartialReset={handlePartialReset}
                disableFirstBoxOfCardFilter={disableFirstBoxOfCardFilter}
              />
            </>
          )}

          {activeCards?.map((card, i) => {
            const contentfulTags = card.metadata.tags;
            let tags: string[] = [];
            let hideCard: boolean = false;

            if (contentfulTags.find((tag) => tag.sys.id === filterVideoTag)) {
              tags = contentfulTags.map((tag) => tag.sys.id).concat(filterAllTag);
            } else {
              tags = contentfulTags.map((tag) => tag.sys.id).concat([filterAllTag, filterEditorialTag]);
            }

            hideCard = activeFilters.length > 0 && !activeFilters.every((filterId) => tags.includes(filterId));

            return (
              <div
                key={i}
                className={classNames(
                  styles.containerCard,
                  hideCard ? styles.hide : undefined,
                  i === 0 && firstCardIsHighlighted
                    ? cardFilter
                      ? styles.firstCardIsHighlightedWithFilter
                      : styles.firstCardIsHighlighted
                    : undefined
                )}
              >
                <EditorialCard entry={card} firstCardIsHighlighted={i === 0 && firstCardIsHighlighted} />
              </div>
            );
          })}
        </>
      </Grid>
    </Container>
  );
}
